/**
 * Connection Store
 *
 * Manages WebSocket connection state and status.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

interface ConnectionState {
  // State
  status: ConnectionStatus;
  lastConnectedAt: number | null;
  reconnectAttempts: number;
  latency: number | null;
  error: string | null;

  // Actions
  setStatus: (status: ConnectionStatus) => void;
  setLatency: (latency: number) => void;
  setError: (error: string | null) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  onConnected: () => void;
  onDisconnected: () => void;
}

export const useConnectionStore = create<ConnectionState>()(
  devtools(
    (set) => ({
      // Initial state
      status: 'disconnected',
      lastConnectedAt: null,
      reconnectAttempts: 0,
      latency: null,
      error: null,

      // Set connection status
      setStatus: (status) => {
        set({ status });
      },

      // Set measured latency
      setLatency: (latency) => {
        set({ latency });
      },

      // Set error message
      setError: (error) => {
        set({ error });
      },

      // Increment reconnect attempts
      incrementReconnectAttempts: () => {
        set((state) => ({ reconnectAttempts: state.reconnectAttempts + 1 }));
      },

      // Reset reconnect attempts
      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 });
      },

      // Handle successful connection
      onConnected: () => {
        set({
          status: 'connected',
          lastConnectedAt: Date.now(),
          reconnectAttempts: 0,
          error: null,
        });
      },

      // Handle disconnection
      onDisconnected: () => {
        set({ status: 'disconnected' });
      },
    }),
    { name: 'connection-store' }
  )
);

// Selectors
export const selectStatus = (state: ConnectionState) => state.status;
export const selectIsConnected = (state: ConnectionState) => state.status === 'connected';
export const selectLatency = (state: ConnectionState) => state.latency;
export const selectError = (state: ConnectionState) => state.error;
